import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  IFilteredPlansRequest,
  IFilteredUnitsRequest,
} from 'src/types/filterPlans.interfaces'
import { IFilteredShellsRequest } from 'src/types/filterShells.interfaces'
import { hasValidFilterValue } from 'src/utils/filterGenerators'

import {
  PLAN_BULK_EXPORT_APPROVALS_HISTORY_REPORT,
  PLAN_BULK_EXPORT_PLANS,
  PLAN_BULK_EXPORT_PLANS_DOWNLOAD,
  PLAN_BULK_EXPORT_PLANS_STATUS,
} from '../../lib/api-routes'
import { instance } from '../../lib/axiosInstance'
import { getFilteredPlansForBulkExport } from '../actions/get-filtered-plans'
import { RootState } from '../stores/store'

interface ExportFile {
  exportedBy: string
  exportedTimestamp: string
  fileId: string
  fileName: string
  fileUrl: string
  status: string
  type: string
}

interface ExportState {
  count: number
  data: ExportFile[]
  error: string | null
  hasNext: boolean
  loading: boolean
}

interface DownloadState {
  error: string | null
  loading: boolean
}

export interface BulkExportResponse {
  createdAt: string
  fileId: string
  fileName: string
  fileUrl: string
  size: number
  uploadedBy: string
}

/** Acknowledgement that an export was queued; the file comes later over SignalR. */
export interface BulkExportAcceptedResponse {
  exportId: string
  status: string
}

/** Payload of the `bulkExportCompleteMessage` SignalR event. */
export interface BulkExportCompleteEvent {
  error?: string
  exportId: string
  fileId?: string
  fileName?: string
  fileUrl?: string
  success: boolean
  userUuid: string
}

/** Outcome of the most recent export this user started, for notifying them. */
export interface BulkExportCompletion {
  error?: string
  exportId: string
  success: boolean
}

interface BulkExportState {
  data: BulkExportResponse | null
  error: string | null
  isLoading: boolean
  lastCompletion: BulkExportCompletion | null
}

interface ApprovalHistoryReportState {
  data: BulkExportResponse | null
  error: string | null
  isLoading: boolean
}

export interface BulkExportFileState {
  approvalHistoryReport: ApprovalHistoryReportState
  bulkExport: BulkExportState
  downloadFile: DownloadState
  exportedExcelFiles: ExportState
}

export const initialState: BulkExportFileState = {
  approvalHistoryReport: {
    data: null,
    error: null,
    isLoading: false,
  },
  bulkExport: {
    data: null,
    error: null,
    isLoading: false,
    lastCompletion: null,
  },
  downloadFile: {
    error: null,
    loading: false,
  },
  exportedExcelFiles: {
    count: 0,
    data: [],
    error: null,
    hasNext: false,
    loading: false,
  },
}

export const bulkExport = createAsyncThunk(
  'bulkExport/exportUserPlans',
  async ({
    exportId,
    ...bulkExportReq
  }: {
    userUuid: string | null
    communityPlanIds: string[]
    exportId: string
    basePlanId?: string
    planFamilyType?: string
  }) => {
    // The response only acknowledges that the export was accepted. Building the
    // workbook for a large selection outlasts the proxy's read timeout, so the
    // file itself arrives later on the `bulkExportCompleteMessage` SignalR
    // event, matched back to this request by exportId (LTGC-5728).
    return instance
      .post<BulkExportAcceptedResponse>(PLAN_BULK_EXPORT_PLANS, bulkExportReq, {
        params: { exportId },
      })
      .then((res) => res?.data)
  }
)

export const bulkExportPlansForShareAndCompare = createAsyncThunk(
  'bulkExport/shareAndCompareExportUserPlans',
  async (
    params: {
      allPlansActiveElevationIndices: Record<string, string>
      planRequestDto?: IFilteredPlansRequest
      selectedFieldsToPrint: string[]
      bulkExportType: string
      userUuid: string
    },
    { getState }
  ) => {
    const state = getState() as RootState
    const { single } = state.FavoritePlans.favoriteBasePlans

    const finalRequestDto =
      params.planRequestDto || state.filteredPlans.planRequestDto

    const filters = Object.fromEntries(
      Object.entries(finalRequestDto?.filters || {}).filter(
        ([, value]) =>
          Object.keys(value).length > 1 && hasValidFilterValue(value)
      )
    )

    const request = {
      ...finalRequestDto,
      allPlansActiveElevationIndices: params.allPlansActiveElevationIndices,
      bulkExportType: params.bulkExportType,
      filters,
      selectedFieldsToPrint: params.selectedFieldsToPrint,
      userUuid: params.userUuid,
    }
    return await getFilteredPlansForBulkExport(request, single)
  }
)

export const bulkExportUnitsForShareAndCompare = createAsyncThunk(
  '/filteredUnits/bulkExport',
  async (
    params: {
      allPlansActiveElevationIndices: Record<string, string>
      unitRequestDto?: IFilteredUnitsRequest
      selectedFieldsToPrint: string[]
      bulkExportType: string
      userUuid: string
    },
    { getState }
  ) => {
    const state = getState() as RootState
    const { units } = state.FavoritePlans.favoriteBasePlans

    const finalRequestDto =
      params.unitRequestDto || state.filteredPlans.unitRequestDto

    const filters = Object.fromEntries(
      Object.entries(finalRequestDto?.filters || {}).filter(
        ([, value]) =>
          Object.keys(value).length > 1 && hasValidFilterValue(value)
      )
    )

    const request = {
      ...finalRequestDto,
      allPlansActiveElevationIndices: params.allPlansActiveElevationIndices,
      bulkExportType: params.bulkExportType,
      filters,
      selectedFieldsToPrint: params.selectedFieldsToPrint,
      userUuid: params.userUuid,
    }
    return await getFilteredPlansForBulkExport(request, units)
  }
)

export const bulkExportShellsForShareAndCompare = createAsyncThunk(
  '/filteredShells/bulkExport',
  async (
    params: {
      allPlansActiveElevationIndices: Record<string, string>
      shellRequestDto?: IFilteredShellsRequest
      selectedFieldsToPrint: string[]
      bulkExportType: string
      userUuid: string
    },
    { getState }
  ) => {
    const state = getState() as RootState
    const { shells } = state.FavoritePlans.favoriteBasePlans

    const finalRequestDto =
      params.shellRequestDto || state.filteredPlans.shellRequestDto
    const filters = Object.fromEntries(
      Object.entries(finalRequestDto?.filters || {}).filter(
        ([, value]) =>
          Object.keys(value).length > 1 && hasValidFilterValue(value)
      )
    )

    const request = {
      ...finalRequestDto,
      allPlansActiveElevationIndices: params.allPlansActiveElevationIndices,
      bulkExportType: params.bulkExportType,
      filters,
      selectedFieldsToPrint: params.selectedFieldsToPrint,
      userUuid: params.userUuid,
    }
    return await getFilteredPlansForBulkExport(request, shells)
  }
)

export const fetchUserExportedFiles = createAsyncThunk<
  ExportState,
  { userUuid: string | null; page: number; size: number; creationDate?: string }
>(
  'bulkExport/fetchUserExportedFiles',
  async ({ creationDate, page, size, userUuid }, { getState }) => {
    const params: Record<string, string | number | null> = {
      page,
      size,
      userUuid,
    }
    if (creationDate) {
      params.creationDate = creationDate
    }

    const result = await instance
      .get<ExportState>(PLAN_BULK_EXPORT_PLANS_STATUS, { params })
      .then(
        (res) =>
          res?.data || {
            count: 1,
            data: [],
            error: null,
            hasNext: false,
            loading: false,
          }
      )

    const { ExcelExportedFile } = getState() as RootState

    const pendingRow = ExcelExportedFile.exportedExcelFiles.data.find(
      (file) => file.status === 'In Progress'
    )

    // If In Progress row merge it into the result from the API
    if (pendingRow) {
      result.data = [pendingRow, ...result.data] // at the top
    }

    return result
  }
)

export const downloadExportedFile = createAsyncThunk(
  'bulkExport/downloadExportedFile',
  async ({ fileName, fileUrl }: { fileUrl: string; fileName: string }) => {
    return instance
      .post<ArrayBuffer>(
        PLAN_BULK_EXPORT_PLANS_DOWNLOAD,
        { fileName, fileUrl },
        { responseType: 'arraybuffer' }
      )
      .then((res) => res?.data || {})
  }
)

export const generateApprovalsHistoryReport = createAsyncThunk(
  'bulkExport/approvalHistoryReport',
  async ({
    timeRange,
    userId,
    userUuid,
  }: {
    timeRange: string
    userId: string
    userUuid: string | null
  }) => {
    return instance
      .post<BulkExportResponse>(PLAN_BULK_EXPORT_APPROVALS_HISTORY_REPORT, {
        timeRange,
        userId,
        userUuid,
      })
      .then((res) => res?.data ?? ([] as unknown as BulkExportResponse))
  }
)

export const exportedFilesSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserExportedFiles.pending, (state) => {
        state.exportedExcelFiles.loading = true
        state.exportedExcelFiles.error = null
      })
      .addCase(fetchUserExportedFiles.fulfilled, (state, action) => {
        state.exportedExcelFiles.loading = false
        state.exportedExcelFiles.data = action.payload.data
        state.exportedExcelFiles.count = action.payload.count
        state.exportedExcelFiles.hasNext = action.payload.hasNext
        state.exportedExcelFiles.error = null
      })
      .addCase(fetchUserExportedFiles.rejected, (state, action) => {
        state.exportedExcelFiles.loading = false
        state.exportedExcelFiles.error =
          action.error.message ?? 'Error fetching exported files'
      })
      .addCase(downloadExportedFile.pending, (state) => {
        state.downloadFile.loading = true
        state.downloadFile.error = null
      })
      .addCase(downloadExportedFile.fulfilled, (state) => {
        state.downloadFile.loading = false
        state.downloadFile.error = null
      })
      .addCase(downloadExportedFile.rejected, (state, action) => {
        state.downloadFile.loading = false
        state.downloadFile.error =
          action.error.message ?? 'Error downloading file'
      })
      .addCase(bulkExport.pending, (state) => {
        state.bulkExport.isLoading = true
        state.bulkExport.error = null
        state.bulkExport.data = null
      })
      // Fulfilled only means the export was accepted — the workbook is still
      // being generated, so there is no file to record yet. It arrives on the
      // `bulkExportCompleteMessage` SignalR event (see bulkExportCompleted).
      .addCase(bulkExport.fulfilled, (state) => {
        state.bulkExport.isLoading = false
        state.bulkExport.error = null
      })
      .addCase(bulkExport.rejected, (state, action) => {
        state.bulkExport.isLoading = false
        state.bulkExport.error =
          action.error.message ?? 'Error during bulk export'
        state.bulkExport.data = null
      })
      .addCase(bulkExportPlansForShareAndCompare.pending, (state) => {
        state.bulkExport.isLoading = true
        state.bulkExport.error = null
        state.bulkExport.data = null
      })
      .addCase(bulkExportPlansForShareAndCompare.fulfilled, (state, action) => {
        state.bulkExport.isLoading = false
        state.bulkExport.data = action.payload
        state.bulkExport.error = null
      })
      .addCase(bulkExportPlansForShareAndCompare.rejected, (state, action) => {
        state.bulkExport.isLoading = false
        state.bulkExport.error =
          action.error.message ?? 'Error during bulk export'
        state.bulkExport.data = null
        state.exportedExcelFiles.data = state.exportedExcelFiles.data.filter(
          (file) => file.fileId !== 'pending'
        )
        state.exportedExcelFiles.count -= 1
      })
      .addCase(bulkExportUnitsForShareAndCompare.pending, (state) => {
        state.bulkExport.isLoading = true
        state.bulkExport.error = null
        state.bulkExport.data = null
      })
      .addCase(bulkExportUnitsForShareAndCompare.fulfilled, (state, action) => {
        state.bulkExport.isLoading = false
        state.bulkExport.data = action.payload
        state.bulkExport.error = null
      })
      .addCase(bulkExportUnitsForShareAndCompare.rejected, (state, action) => {
        state.bulkExport.isLoading = false
        state.bulkExport.error =
          action.error.message ?? 'Error during bulk export'
        state.bulkExport.data = null
        state.exportedExcelFiles.data = state.exportedExcelFiles.data.filter(
          (file) => file.fileId !== 'pending'
        )
        state.exportedExcelFiles.count -= 1
      })
      .addCase(bulkExportShellsForShareAndCompare.pending, (state) => {
        state.bulkExport.isLoading = true
        state.bulkExport.error = null
        state.bulkExport.data = null
      })
      .addCase(
        bulkExportShellsForShareAndCompare.fulfilled,
        (state, action) => {
          state.bulkExport.isLoading = false
          state.bulkExport.data = action.payload
          state.bulkExport.error = null
        }
      )
      .addCase(bulkExportShellsForShareAndCompare.rejected, (state, action) => {
        state.bulkExport.isLoading = false
        state.bulkExport.error =
          action.error.message ?? 'Error during bulk export'
        state.bulkExport.data = null
        state.exportedExcelFiles.data = state.exportedExcelFiles.data.filter(
          (file) => file.fileId !== 'pending'
        )
        state.exportedExcelFiles.count -= 1
      })
      .addCase(generateApprovalsHistoryReport.pending, (state) => {
        state.approvalHistoryReport.isLoading = true
        state.approvalHistoryReport.error = null
        state.approvalHistoryReport.data = null
      })
      .addCase(generateApprovalsHistoryReport.fulfilled, (state, action) => {
        state.approvalHistoryReport.isLoading = false
        state.approvalHistoryReport.data = action.payload
        state.approvalHistoryReport.error = null
      })
      .addCase(generateApprovalsHistoryReport.rejected, (state, action) => {
        state.approvalHistoryReport.isLoading = false
        state.approvalHistoryReport.error =
          action.error.message ?? 'Error generating approval history report'
        state.approvalHistoryReport.data = null
        state.exportedExcelFiles.data = state.exportedExcelFiles.data.filter(
          (file) => file.fileId !== 'pending'
        )
        state.exportedExcelFiles.count -= 1
      })
  },
  initialState,
  name: 'bulkExport',
  reducers: {
    //  add a placeholder "In Progress"
    addInProgressExport: (state, action) => {
      const { exportedTimestamp, fileId, type, userUuid } = action.payload
      state.exportedExcelFiles.data.push({
        exportedBy: userUuid,
        exportedTimestamp,
        fileId,
        fileName: 'Pending',
        fileUrl: '',
        status: 'In Progress',
        type,
      })
      state.exportedExcelFiles.count += 1
    },

    // Resolves an export once the server reports it finished over SignalR:
    // promotes the "In Progress" placeholder to a downloadable row, or drops it
    // if the export failed. The outcome is recorded either way so the user is
    // still told about it when the placeholder is gone — a refetch of the
    // exports list drops rows this session added optimistically.
    bulkExportCompleted: (
      state,
      action: PayloadAction<BulkExportCompleteEvent>
    ) => {
      const { error, exportId, fileId, fileName, fileUrl, success } =
        action.payload

      state.bulkExport.isLoading = false
      state.bulkExport.error = success
        ? null
        : error ?? 'Error during bulk export'
      state.bulkExport.lastCompletion = { error, exportId, success }

      const index = state.exportedExcelFiles.data.findIndex(
        (file) => file.fileId === exportId
      )
      if (index === -1) {
        return
      }

      if (!success) {
        state.exportedExcelFiles.data.splice(index, 1)
        state.exportedExcelFiles.count = Math.max(
          0,
          state.exportedExcelFiles.count - 1
        )
        return
      }

      state.exportedExcelFiles.data[index] = {
        ...state.exportedExcelFiles.data[index],
        fileId: fileId ?? exportId,
        fileName: fileName ?? '',
        fileUrl: fileUrl ?? '',
        status: 'Available',
      }
    },

    // to remove the "In Progress" placeholder if the export fails
    removeInProgressExport: (state, action) => {
      state.exportedExcelFiles.data = state.exportedExcelFiles.data.filter(
        (file) => file.fileId !== action.payload.fileId
      )
      state.exportedExcelFiles.count -= 1
    },

    resetBulkExport: (state) => {
      state.bulkExport.data = null
      state.bulkExport.error = null
      state.bulkExport.isLoading = false
      state.bulkExport.lastCompletion = null
    },
    // update the status to "Available" once the file is uploaded
    updateExportStatus: (state, action) => {
      const index = state.exportedExcelFiles.data.findIndex(
        (file) => file.fileId === action.payload.fileId
      )

      if (index !== -1) {
        state.exportedExcelFiles.data[index].fileId = action.payload.newFileId
        state.exportedExcelFiles.data[index].fileName = action.payload.fileName
        state.exportedExcelFiles.data[index].fileUrl = action.payload.fileUrl
        state.exportedExcelFiles.data[index].status = 'Available'
      }
    },
  },
})

export const {
  addInProgressExport,
  bulkExportCompleted,
  removeInProgressExport,
  resetBulkExport,
  updateExportStatus,
} = exportedFilesSlice.actions

export const { reducer: exportedFilesReducer } = exportedFilesSlice
